/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import Loading from '../../components/Loading';
import axios from '../../services/axios';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import Modal from '../../components/Modal/Modal';
import {
   CtaButton,
   DangerButton,
   GhostButton,
   IconButton,
   PrimaryButton,
   SmallButton,
   TabButton,
} from '../../styles/ui/Button';
import { LinkButton, PillLink, TextLink } from '../../styles/ui/Link';
import { EntityActionBar, ActionBarSpacer } from '../../styles/ui/ActionBar';
import InputCpfCnpj from '../../components/MaskedInput/InputCpfCnpj';
import { validateCpfCnpj } from '../../utils/validateCpfCnpj';
import { formatBRL, formatDocForDisplay } from '../../utils/format';
import { listBankAccounts } from '../../services/finance/bankAccountsApi';
import {
   createCharge,
   createInstallment,
   cancelCharge,
   cancelChargeGroup,
   getCharge,
   getChargeTimeline,
   settleCharge,
} from '../../services/finance/billingApi';
import CancelReasonModal from '../Finance/components/CancelReasonModal';
import BankAccountTag from '../Finance/components/BankAccountTag';
import BankAccountCards from './components/BankAccountCards';
import IssueConfirmModal from './components/IssueConfirmModal';
// Mesma família (Financeiro) — a pasta é que é legada/flat. O dicionário de
// `code` das contas bancárias é o mesmo que a emissão precisa consultar.
import { financeErrorMessage, readApiError } from '../Finance/utils/apiError';
import {
   isAptToIssue,
   issuanceBlockReason,
} from '../Finance/utils/bankAccount';

// ─── Estilos locais ───────────────────────────────────────────

// Aviso âmbar do topo do form de emissão: sem conta recebedora apta, nenhuma
// cobrança sai. Variante de warning contextual — fica local por decisão do
// FC7 G.2 (avisos âmbar não são variante do kit de botões).
const IssuanceWarning = styled.div`
   border: 1px solid rgba(133, 79, 11, 0.35);
   background: rgba(133, 79, 11, 0.08);
   color: #854f0b;
   border-radius: 10px;
   padding: 12px 14px;
   font-size: 12.5px;
   line-height: 1.6;
   margin-bottom: 14px;
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;

   strong {
      font-weight: 800;
   }
`;

const FieldHint = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   margin-top: 2px;
`;

// Erro de campo devolvido pelo backend (422 com `field`) — telefone/e-mail.
const FieldErrorText = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.danger ?? '#b71c1c'};
   margin-top: 2px;
`;

const BackRow = styled.div`
   margin-bottom: 12px;
`;

// Chip da empresa emissora escolhida (modo criação).
const IssuerChip = styled.div`
   display: inline-flex;
   align-items: baseline;
   gap: 8px;
   flex-wrap: wrap;
   margin-top: 8px;
   padding: 6px 12px;
   border-radius: 999px;
   border: 1px solid rgba(64, 152, 239, 0.45);
   background: rgba(64, 152, 239, 0.1);
   width: fit-content;

   .label {
      font-size: 0.64rem;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   }
   .name {
      font-size: 0.82rem;
      font-weight: 800;
   }
   .doc {
      font-size: 0.72rem;
      font-variant-numeric: tabular-nums;
      color: ${({ theme }) => theme?.colors?.textMuted ?? '#555'};
   }
`;

// Caixa que imita o input desabilitado da ficha, para exibir conteúdo rico
// (a empresa emissora tem duas linhas: apelido + CNPJ) sem virar um <input>.
const ReadOnlyBox = styled.div`
   display: flex;
   align-items: center;
   min-height: 40px;
   padding: 6px 10px;
   border-radius: 10px;
   border: 1px solid
      ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
   background: #dbdbdb;
   overflow: hidden;
`;

const StatusPill = styled.span`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   padding: 4px 10px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 800;
   border: 1px solid ${({ $color }) => $color ?? 'rgba(0,0,0,0.15)'};
   background: ${({ $bg }) => $bg ?? 'rgba(0,0,0,0.06)'};
   color: ${({ $text }) => $text ?? '#444'};
`;

const STATUS_CONFIG = {
   PENDING: {
      label: 'Pendente',
      bg: 'rgba(255,193,7,0.15)',
      border: 'rgba(255,193,7,0.5)',
      text: '#7d5a00',
   },
   RECEIVED: {
      label: 'Recebido',
      bg: 'rgba(46,125,50,0.12)',
      border: 'rgba(46,125,50,0.35)',
      text: '#1b5e20',
   },
   OVERDUE: {
      label: 'Vencido',
      bg: 'rgba(211,47,47,0.12)',
      border: 'rgba(211,47,47,0.35)',
      text: '#b71c1c',
   },
   CANCELLED: {
      label: 'Cancelado',
      bg: 'rgba(0,0,0,0.06)',
      border: 'rgba(0,0,0,0.15)',
      text: '#444',
   },
   REFUNDED: {
      label: 'Estornado',
      bg: 'rgba(123,31,162,0.1)',
      border: 'rgba(123,31,162,0.3)',
      text: '#4a148c',
   },
};

const COLLECTION_CONFIG = {
   ATIVA: {
      label: 'Ativa',
      bg: 'rgba(2,136,209,0.1)',
      border: 'rgba(2,136,209,0.3)',
      text: '#01579b',
   },
   PAGA: {
      label: 'Paga',
      bg: 'rgba(46,125,50,0.12)',
      border: 'rgba(46,125,50,0.35)',
      text: '#1b5e20',
   },
   CANCELADA: {
      label: 'Cancelada',
      bg: 'rgba(0,0,0,0.06)',
      border: 'rgba(0,0,0,0.15)',
      text: '#444',
   },
   RENEGOCIADA: {
      label: 'Renegociada',
      bg: 'rgba(230,81,0,0.1)',
      border: 'rgba(230,81,0,0.3)',
      text: '#bf360c',
   },
   CONSULTH: {
      label: 'Consulth',
      bg: 'rgba(2,136,209,0.1)',
      border: 'rgba(2,136,209,0.3)',
      text: '#01579b',
   },
   REVENDEDOR: {
      label: 'Revendedor',
      bg: 'rgba(230,81,0,0.1)',
      border: 'rgba(230,81,0,0.3)',
      text: '#bf360c',
   },
   JURIDICO: {
      label: 'Jurídico',
      bg: 'rgba(81,45,168,0.12)',
      border: 'rgba(81,45,168,0.35)',
      text: '#311b92',
   },
};

const InstallmentPreview = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   margin-top: 4px;
`;

const InstallmentRow = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 8px 12px;
   background: rgba(2, 136, 209, 0.05);
   border: 1px solid rgba(2, 136, 209, 0.2);
   border-radius: 6px;
   font-size: 13px;
`;

const InstallmentBadge = styled.span`
   background: rgba(2, 136, 209, 0.15);
   color: #01579b;
   border-radius: 999px;
   padding: 2px 8px;
   font-size: 11px;
   font-weight: 800;
   min-width: 36px;
   text-align: center;
`;

// ─── Timeline ─────────────────────────────────────────────────
const TimelineFeed = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const TimelineItem = styled.div`
   display: flex;
   gap: 12px;
   padding: 12px 14px;
   border-radius: 8px;
   background: ${({ $type }) =>
      $type === 'update' ? 'rgba(0,0,0,0.02)' : 'rgba(156,39,176,0.04)'};
   border: 1px solid
      ${({ $type }) =>
         $type === 'update' ? 'rgba(0,0,0,0.08)' : 'rgba(156,39,176,0.15)'};
`;

const TimelineAvatar = styled.div`
   width: 36px;
   height: 36px;
   border-radius: 50%;
   background: ${({ $type }) =>
      $type === 'update' ? 'rgba(2,136,209,0.15)' : 'rgba(156,39,176,0.12)'};
   color: ${({ $type }) => ($type === 'update' ? '#01579b' : '#6a1b9a')};
   display: flex;
   align-items: center;
   justify-content: center;
   font-weight: 800;
   font-size: 13px;
   flex-shrink: 0;
`;

const TimelineContent = styled.div`
   flex: 1;
`;

const TimelineMeta = styled.div`
   font-size: 11px;
   color: #888;
   margin-bottom: 4px;
`;

const TimelineText = styled.div`
   font-size: 13px;
   color: #333;
   white-space: pre-wrap;
`;

// Tabs
const TabBar = styled.div`
   display: flex;
   gap: 0;
   border-bottom: 2px solid rgba(0, 0, 0, 0.08);
   margin-bottom: 16px;
`;

const Tab = TabButton;

const TabCount = styled.span`
   margin-left: 6px;
   font-size: 10px;
   font-weight: 800;
   background: rgba(0, 0, 0, 0.08);
   border-radius: 999px;
   padding: 1px 6px;
`;

const UpdateBox = styled.div`
   margin-bottom: 20px;
   display: flex;
   flex-direction: column;
   gap: 8px;

   textarea {
      width: 100%;
      min-height: 80px;
      padding: 10px;
      border: 1px solid rgba(0, 0, 0, 0.2);
      border-radius: 6px;
      font-size: 13px;
      resize: vertical;
      font-family: inherit;
      box-sizing: border-box;
   }
`;

// ─── Helpers ──────────────────────────────────────────────────
function formatDate(value) {
   if (!value) return '—';
   const [y, m, d] = String(value).substring(0, 10).split('-');
   return `${d}/${m}/${y}`;
}

function formatDateTime(value) {
   if (!value) return '—';
   return new Date(value).toLocaleString('pt-BR');
}

function formatCurrency(value) {
   return Number(value || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

// Taxa do boleto DERIVADA no front (recebido − líquido) — o backend não manda
// esse campo (handoff 0105 §2.1). Arredonda em centavos pra não vazar resíduo
// de float. `null` quando qualquer lado falta (título antigo sem backfill).
function boletoFee(charge) {
   if (charge?.amount_received == null || charge?.net_amount_received == null) {
      return null;
   }
   return (
      Math.round(
         (Number(charge.amount_received) - Number(charge.net_amount_received)) *
            100
      ) / 100
   );
}

function getInitials(name) {
   if (!name) return '?';
   return name
      .split(' ')
      .slice(0, 2)
      .map((n) => n[0])
      .join('')
      .toUpperCase();
}

function StatusBadge({ value, config }) {
   const cfg = config[value] ?? { label: value };
   return (
      <StatusPill $bg={cfg.bg} $color={cfg.border} $text={cfg.text}>
         {cfg.label}
      </StatusPill>
   );
}

// Gera datas de vencimento das parcelas a partir da primeira
function buildInstallmentDates(firstDueDate, count) {
   if (!firstDueDate || !count || count < 1) return [];
   const dates = [];
   const [y, m, d] = firstDueDate.split('-').map(Number);
   for (let i = 0; i < count; i += 1) {
      const date = new Date(y, m - 1 + i, d);
      const yy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      dates.push(`${yy}-${mm}-${dd}`);
   }
   return dates;
}

// ─── Estado inicial do formulário de criação ──────────────────
function getEmptyForm() {
   return {
      type: 'single',
      customer_name: '',
      customer_doc: '',
      customer_email: '',
      customer_phone: '',
      order_number: '',
      nfe_number: '',
      origin_id: '', // integer no payload; string vazia = sem origem
      // Conta recebedora (adendo do handoff 0079): OBRIGATÓRIO no POST, mesmo
      // havendo uma única conta cadastrada — a escolha é sempre explícita, para
      // preservar a flexibilidade multi-CNPJ.
      bank_account_id: '',
      // Legado do modelo singleton (chave única no .env): nunca teve input na
      // tela e sempre foi enviado como undefined. Fica até o backend confirmar
      // que pode sair do payload — quem manda hoje é bank_account_id.
      asaas_account_label: '',
      notes: '',
      installment_value: '',
      due_date: '',
      total_value: '',
      installment_count: '',
      first_due_date: '',
      description: '',
   };
}

// ─── Componente principal ─────────────────────────────────────
export default function BillingCharge() {
   const { id } = useParams();
   const navigate = useNavigate();
   const location = useLocation();
   const isCreateMode = !id;
   const permissions = useSelector(
      (state) => state.auth.user.permissions || []
   );
   const canCreate = permissions.includes('finance:charge:create');
   const canUpdate = permissions.includes('finance:charge:update');
   const canCancel = permissions.includes('finance:charge:cancel');
   const canAddUpdate = permissions.includes('finance:interaction:create');
   const canDeleteUpdate = permissions.includes('finance:interaction:delete');
   const canSettle = permissions.includes('finance:charge:settle');

   // ─── Estado ───────────────────────────────────────────────
   const [charge, setCharge] = useState(null);
   const [isLoading, setIsLoading] = useState(!isCreateMode);
   const [isSaving, setIsSaving] = useState(false);
   const [editOn, setEditOn] = useState(isCreateMode);

   // ─── Lista de origens ativas (para selects de criação e edição)
   const [billingOrigins, setBillingOrigins] = useState([]);

   // ─── Contas recebedoras ativas (só no modo criação) ───────
   // Diferente das origens, a falha aqui NÃO pode ser silenciosa: sem conta
   // selecionada o backend recusa a emissão, então a tela precisa dizer por quê.
   const [bankAccounts, setBankAccounts] = useState([]);
   const [bankAccountsError, setBankAccountsError] = useState(null);
   const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);

   // Campos do form de criação
   const [form, setForm] = useState(getEmptyForm());
   // Erros de campo devolvidos pelo backend na emissão (422 com `field`) —
   // hoje só telefone/e-mail, validados antes de qualquer escrita. Vão INLINE,
   // nunca em toast (FC4 B.3).
   const [createFieldErrors, setCreateFieldErrors] = useState({});

   // Modal de duplicata
   const [duplicateChargeId, setDuplicateChargeId] = useState(null);

   // Conferência final antes de emitir
   const [showIssueConfirm, setShowIssueConfirm] = useState(false);

   // Campos editáveis em modo visualização/edição
   const [nextAction, setNextAction] = useState('');
   const [nextContactDate, setNextContactDate] = useState('');
   const [collectionStatus, setCollectionStatus] = useState('');
   const [notes, setNotes] = useState('');
   const [dueDate, setDueDate] = useState('');
   const [nfeNumber, setNfeNumber] = useState('');
   const [editInstallmentValue, setEditInstallmentValue] = useState('');
   const [editOriginId, setEditOriginId] = useState(''); // id da origem em modo edição

   // Timeline mesclada
   const [feed, setFeed] = useState([]);
   const [loadingFeed, setLoadingFeed] = useState(false);
   const [activeTab, setActiveTab] = useState('atendimentos');

   // Novo atendimento
   const [updateContent, setUpdateContent] = useState('');
   const [isAddingUpdate, setIsAddingUpdate] = useState(false);

   // Modal cancelamento
   const [showCancelModal, setShowCancelModal] = useState(false);

   // Modal de baixa manual
   const [showSettleModal, setShowSettleModal] = useState(false);
   const [settleDate, setSettleDate] = useState('');

   // PIX copia-e-cola (lazy: só busca quando o usuário clica em "Gerar")
   const [isPixLoading, setIsPixLoading] = useState(false);
   const [pixCode, setPixCode] = useState(null);

   // ─── Carrega origens ativas no mount ──────────────────────
   useEffect(() => {
      const fetchBillingOrigins = async () => {
         try {
            const response = await axios.get('/billing/origins');
            setBillingOrigins(response?.data?.data?.origins ?? []);
         } catch {
            // falha silenciosa — selects ficam vazios mas não bloqueiam a tela
         }
      };
      fetchBillingOrigins();
   }, []);

   // ─── Carrega contas recebedoras ativas (modo criação) ─────
   useEffect(() => {
      if (!isCreateMode) return;

      const fetchBankAccounts = async () => {
         setLoadingBankAccounts(true);
         try {
            const list = await listBankAccounts({ status: 'active' });
            setBankAccounts(list);
            setBankAccountsError(null);
            // Pré-seleção só quando existe UMA conta APTA (ativa + com chave):
            // uma conta ativa sem credencial não emite, então pré-selecioná-la
            // levaria direto ao 422. O campo segue visível e o valor vai
            // EXPLÍCITO no payload (nunca implícito).
            const apt = list.filter(isAptToIssue);
            if (apt.length === 1) {
               setForm((prev) => ({
                  ...prev,
                  bank_account_id: String(apt[0].id),
               }));
            }
         } catch (err) {
            setBankAccounts([]);
            // 403 aqui é o caso mais provável: emitir passou a depender de
            // finance:bank_account:read. Sem essa permissão a tela não tem como
            // montar o select — melhor dizer isso do que mostrar lista vazia.
            setBankAccountsError(
               err?.response?.status === 403
                  ? 'Você não tem permissão para listar as contas recebedoras. Peça a permissão finance:bank_account:read ao administrador.'
                  : financeErrorMessage(
                       err,
                       'Não foi possível carregar as contas recebedoras.'
                    )
            );
         } finally {
            setLoadingBankAccounts(false);
         }
      };

      fetchBankAccounts();
   }, [isCreateMode]);

   // Conta escolhida — usada para mostrar o titular abaixo do select, que é o
   // que dá segurança ao operador de que escolheu o CNPJ certo.
   const selectedBankAccount = useMemo(
      () =>
         bankAccounts.find(
            (account) => String(account.id) === String(form.bank_account_id)
         ) ?? null,
      [bankAccounts, form.bank_account_id]
   );

   // Contas que realmente conseguem emitir. É esta contagem — não o total —
   // que decide travar o form: uma conta ativa SEM chave aparece na lista, mas
   // não serve para emitir nada.
   const aptBankAccounts = useMemo(
      () => bankAccounts.filter(isAptToIssue),
      [bankAccounts]
   );
   const hasBlockedBankAccounts = bankAccounts.length > aptBankAccounts.length;

   // ─── Preview de parcelas (modo criação) ───────────────────
   const installmentDates = useMemo(() => {
      if (form.type !== 'installment') return [];
      const count = parseInt(form.installment_count, 10);
      return buildInstallmentDates(form.first_due_date, count);
   }, [form.type, form.first_due_date, form.installment_count]);

   const installmentValue = useMemo(() => {
      if (form.type !== 'installment') return 0;
      const total = parseFloat(form.total_value) || 0;
      const count = parseInt(form.installment_count, 10) || 1;
      return parseFloat((total / count).toFixed(2));
   }, [form.type, form.total_value, form.installment_count]);

   // Resumo da conferência final. Reflete o tipo escolhido: avulso mostra valor
   // e vencimento; parcelamento mostra o total e o desdobramento em parcelas.
   // Declarado DEPOIS de installmentValue de propósito: o array de dependências
   // é avaliado na hora, e referenciar um const posterior estoura TDZ.
   const issueSummary = useMemo(() => {
      const rows = [
         { label: 'Cliente', value: form.customer_name || '—' },
         {
            label: 'Tipo',
            value: form.type === 'single' ? 'Boleto avulso' : 'Parcelamento',
         },
      ];

      if (form.type === 'single') {
         rows.push({
            label: 'Valor',
            value: form.installment_value
               ? formatCurrency(parseFloat(form.installment_value))
               : '—',
         });
         rows.push({
            label: 'Vencimento',
            value: formatDate(form.due_date),
         });
      } else {
         rows.push({
            label: 'Valor total',
            value: form.total_value
               ? formatCurrency(parseFloat(form.total_value))
               : '—',
         });
         rows.push({
            label: 'Parcelas',
            value: `${form.installment_count || '—'}x de ${formatCurrency(installmentValue)}`,
         });
         rows.push({
            label: '1º vencimento',
            value: formatDate(form.first_due_date),
         });
      }

      if (form.order_number) {
         rows.push({ label: 'Nº pedido', value: form.order_number });
      }
      if (form.nfe_number) {
         rows.push({ label: 'Nº nota', value: form.nfe_number });
      }

      return rows;
   }, [form, installmentValue]);

   // ─── Subtitle dinâmico ────────────────────────────────────
   const headerSubtitle = useMemo(() => {
      if (isCreateMode) return 'Novo boleto • Modo criação';
      if (!charge) return '';
      const parts = [];
      if (charge.order_number) parts.push(`Pedido ${charge.order_number}`);
      if (charge.installment_count > 1) {
         parts.push(
            `Parcela ${charge.installment_number} de ${charge.installment_count}`
         );
      } else {
         parts.push('Avulso');
      }
      parts.push(`Vencimento ${formatDate(charge.due_date)}`);
      parts.push(editOn ? '• Modo edição' : '• Visualização');
      return parts.join(' · ');
   }, [isCreateMode, charge, editOn]);

   // ─── Carrega timeline mesclada ────────────────────────────
   const fetchTimeline = useCallback(async (chargeId) => {
      setLoadingFeed(true);
      try {
         setFeed(await getChargeTimeline(chargeId));
      } catch {
         // falha silenciosa — timeline não é crítica
      } finally {
         setLoadingFeed(false);
      }
   }, []);

   // ─── Carrega boleto em modo visualização ──────────────────
   useEffect(() => {
      if (isCreateMode) return;
      const fetchCharge = async () => {
         try {
            const data = await getCharge(id);
            setCharge(data);
            setNextAction(data?.next_action ?? '');
            setNextContactDate(data?.next_contact_date ?? '');
            setCollectionStatus(data?.collection_status ?? '');
            setDueDate(data?.due_date ?? '');
            setNfeNumber(data?.nfe_number ?? '');
            setEditInstallmentValue(data?.installment_value ?? '');
            setEditOriginId(data?.billingOrigin?.id ?? '');
            setNotes(data?.notes ?? '');
            setEditOn(false);
            fetchTimeline(id);
         } catch (err) {
            toast.error('Erro ao carregar boleto.');
            if (err?.response?.status === 404) navigate('/billing');
         } finally {
            setIsLoading(false);
         }
      };
      fetchCharge();
   }, [id, isCreateMode, navigate, fetchTimeline]);

   // ─── Handlers form criação ────────────────────────────────
   const handleFormChange = (e) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));
      // Editar o campo limpa o erro que o backend apontou nele.
      setCreateFieldErrors((prev) => {
         if (!prev[name]) return prev;
         const next = { ...prev };
         delete next[name];
         return next;
      });
   };

   const handleResetForm = () => {
      setForm(getEmptyForm());
      setCreateFieldErrors({});
   };

   // ─── Handler de baixa manual ──────────────────────────────
   const handleSettle = async () => {
      if (!settleDate) {
         toast.warn('Informe a data de recebimento.');
         return;
      }
      setIsSaving(true);
      try {
         await settleCharge(id, settleDate);
         toast.success('Baixa manual registrada com sucesso!');
         setShowSettleModal(false);
         // Re-GET em vez de usar o charge do retorno: baixa não é otimista — o
         // status só muda quando o webhook chegar, e o re-GET revalida tudo.
         setCharge(await getCharge(id));
         fetchTimeline(id);
      } catch (err) {
         // `errors` é ARRAY no contrato JSend — o acesso antigo (errors.message)
         // era sempre undefined e mascarava a causa real no texto genérico.
         // Agora passa pelo dicionário do Financeiro, que cobre inclusive o
         // charge_without_bank_account de cobrança sem conta vinculada.
         toast.error(
            financeErrorMessage(err, 'Erro ao registrar baixa manual.')
         );
      } finally {
         setIsSaving(false);
      }
   };

   // ─── Gerar PIX copia-e-cola (busca no backend e revela o input)
   const handleGeneratePix = async () => {
      setIsPixLoading(true);
      try {
         const response = await axios.get(`/billing/${id}/pix`);
         const pix = response?.data?.data?.pix_copy_paste;
         if (!pix) {
            toast.error(
               'Não foi possível obter o código PIX. Tente novamente em alguns instantes.'
            );
            return;
         }
         setPixCode(pix);
      } catch (err) {
         const status = err?.response?.status;
         const code = err?.response?.data?.errors?.[0]?.code;
         if (status === 404) {
            toast.error('Boleto não encontrado.');
         } else if (status === 422 && code === 'asaas_not_created') {
            toast.warn(
               'Este boleto ainda está sendo processado. Aguarde alguns segundos e tente novamente.'
            );
         } else {
            // Não engolir a causa num texto fixo: o backend pode devolver
            // charge_without_bank_account (cobrança antiga sem conta vinculada),
            // que o texto genérico esconderia.
            toast.error(
               financeErrorMessage(
                  err,
                  'Não foi possível obter o código PIX. Tente novamente em alguns instantes.'
               )
            );
         }
      } finally {
         setIsPixLoading(false);
      }
   };

   // ─── Voltar ───────────────────────────────────────────────
   // Via histórico: a URL da listagem carrega filtros, ordenação e página
   // (gravados com replace:true), então voltar restaura o lugar exato de onde o
   // operador saiu. Deep-link direto cai no fallback (navegacao.md §1.6).
   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate('/billing');
   };

   // ─── Copiar PIX para a área de transferência ──────────────
   const handleCopyPix = async () => {
      if (!pixCode) return;
      try {
         if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(pixCode);
            toast.success('Código PIX copiado!');
         } else {
            toast.error('Cópia automática indisponível neste navegador.');
         }
      } catch {
         toast.error('Não foi possível copiar. Tente novamente.');
      }
   };

   // ─── Criar boleto ─────────────────────────────────────────
   // O submit NÃO emite: valida e abre a conferência final. Emitir pelo CNPJ
   // errado exige cancelar e reemitir com o cliente já notificado, então o erro
   // precisa ser interceptado antes do POST, não explicado depois.
   const handleCreate = (e) => {
      e.preventDefault();

      if (!form.customer_name) {
         toast.error('Informe o nome do cliente.');
         return;
      }

      // Conta recebedora é obrigatória desde o adendo do 0079 — o backend
      // recusa sem ela. Barra aqui para não gastar uma ida ao servidor.
      if (!form.bank_account_id) {
         toast.error('Selecione a conta recebedora do boleto.');
         return;
      }

      // Rede de segurança: a opção inapta já vai desabilitada no select, mas se
      // o estado escapar (conta que perdeu a chave enquanto o form estava
      // aberto), avisa aqui em vez de deixar o 422 explicar.
      if (selectedBankAccount && !isAptToIssue(selectedBankAccount)) {
         toast.error(
            `A conta selecionada está ${issuanceBlockReason(selectedBankAccount)} e não pode emitir.`
         );
         return;
      }

      // Doc é opcional, mas se preenchido precisa ser válido (mesma regra do backend).
      if (form.customer_doc) {
         const docCheck = validateCpfCnpj(form.customer_doc);
         if (!docCheck.valid) {
            toast.error(docCheck.reason);
            return;
         }
      }

      if (form.type === 'single') {
         if (!form.installment_value) {
            toast.error('Informe o valor do boleto.');
            return;
         }
         if (!form.due_date) {
            toast.error('Informe a data de vencimento.');
            return;
         }
      } else {
         if (!form.total_value) {
            toast.error('Informe o valor total.');
            return;
         }
         if (
            !form.installment_count ||
            parseInt(form.installment_count, 10) < 2
         ) {
            toast.error('Informe o número de parcelas (mínimo 2).');
            return;
         }
         if (!form.first_due_date) {
            toast.error('Informe a data do primeiro vencimento.');
            return;
         }
      }

      // Tudo válido → conferência final (a emissão acontece em performCreate).
      setShowIssueConfirm(true);
   };

   // Emissão de fato — só é alcançada pelo "Confirmar e emitir" do modal.
   const performCreate = async () => {
      // origin_id: converte para inteiro se preenchido, senão omite do payload
      const parsedOriginId = form.origin_id
         ? parseInt(form.origin_id, 10)
         : undefined;

      // Campos comuns aos dois tipos, incluindo a conta recebedora.
      const commonPayload = {
         customer_name: form.customer_name,
         customer_doc: form.customer_doc || undefined,
         customer_email: form.customer_email || undefined,
         customer_phone: form.customer_phone || undefined,
         order_number: form.order_number || undefined,
         nfe_number: form.nfe_number || undefined,
         origin_id: parsedOriginId,
         bank_account_id: parseInt(form.bank_account_id, 10),
         asaas_account_label: form.asaas_account_label || undefined,
      };

      setIsSaving(true);
      try {
         if (form.type === 'single') {
            const { isDuplicate, charge: returnedCharge } = await createCharge({
               ...commonPayload,
               installment_value: parseFloat(form.installment_value),
               due_date: form.due_date,
               notes: form.notes || undefined,
            });

            // 200 (em vez de 201) = já existe boleto equivalente → modal.
            if (isDuplicate) {
               setDuplicateChargeId(returnedCharge.id);
               return;
            }

            toast.success('Boleto criado com sucesso!');
            navigate(`/billing/${returnedCharge.id}`);
         } else {
            const {
               isDuplicate,
               charge: returnedCharge,
               group: returnedGroup,
            } = await createInstallment({
               ...commonPayload,
               total_value: parseFloat(form.total_value),
               installment_count: parseInt(form.installment_count, 10),
               first_due_date: form.first_due_date,
               description: form.description || undefined,
            });

            if (isDuplicate) {
               setDuplicateChargeId(returnedCharge.id);
               return;
            }

            toast.success(
               `Parcelamento criado! ${form.installment_count}x de ${formatCurrency(installmentValue)}`
            );
            navigate(`/billing/group/${returnedGroup.id}`);
         }
      } catch (err) {
         // Telefone/e-mail inválidos voltam 422 COM `field` (o backend valida os
         // dois de uma vez, antes de qualquer escrita) → inline no campo.
         const { fieldErrors } = readApiError(err);
         if (Object.keys(fieldErrors).length) {
            setCreateFieldErrors(fieldErrors);
            return;
         }
         // Demais casos discriminam por `code`: conta inexistente, inativa ou
         // sem chave ativa têm caminho de correção próprio — o dicionário do
         // módulo Financeiro já aponta a tela de contas bancárias.
         toast.error(financeErrorMessage(err, 'Erro ao criar boleto.'));
      } finally {
         setIsSaving(false);
         // Fecha a conferência em qualquer desfecho: no erro de campo, o
         // operador precisa voltar ao formulário para corrigir.
         setShowIssueConfirm(false);
      }
   };

   // ─── Salvar edição ────────────────────────────────────────
   const handleSave = async () => {
      if (charge?.status === 'RECEIVED' || charge?.status === 'CANCELLED') {
         toast.error('Este boleto não pode ser editado.');
         setEditOn(false);
         return;
      }
      setIsSaving(true);
      try {
         // origin_id: envia apenas se foi alterado em relação ao valor atual
         const currentOriginId = charge?.billingOrigin?.id ?? '';
         const originIdChanged =
            String(editOriginId) !== String(currentOriginId);

         const response = await axios.put(`/billing/${id}`, {
            next_action: nextAction || null,
            next_contact_date: nextContactDate || null,
            collection_status: collectionStatus,
            notes: notes || null,
            due_date: dueDate !== charge.due_date ? dueDate : undefined,
            // Normaliza null e string vazia como equivalentes antes de comparar,
            // evitando log e update desnecessários quando o campo não foi alterado.
            nfe_number:
               (nfeNumber || null) !== (charge.nfe_number || null)
                  ? nfeNumber || null
                  : undefined,
            installment_value:
               parseFloat(editInstallmentValue) !==
               parseFloat(charge.installment_value)
                  ? editInstallmentValue
                  : undefined,
            // Envia origin_id como inteiro se alterado; null para limpar a origem
            origin_id: originIdChanged
               ? editOriginId
                  ? parseInt(editOriginId, 10)
                  : null
               : undefined,
         });

         const updatedCharge = response?.data?.data?.charge;
         const siblingsUpdated = response?.data?.data?.siblings_updated ?? 0;
         const responseMessage =
            response?.data?.message ?? 'Boleto atualizado.';

         setCharge(updatedCharge);
         setDueDate(updatedCharge?.due_date ?? '');
         setNfeNumber(updatedCharge?.nfe_number ?? '');
         setEditInstallmentValue(updatedCharge?.installment_value ?? '');
         setEditOriginId(updatedCharge?.billingOrigin?.id ?? '');
         setEditOn(false);
         fetchTimeline(id);

         // Toast dinâmico: usa a mensagem do backend que já carrega o count
         toast.success(
            siblingsUpdated > 0 ? responseMessage : 'Boleto atualizado.'
         );
      } catch (err) {
         // Atualizar autentica na conta que emitiu a cobrança — pode voltar
         // charge_without_bank_account em registro antigo (adendo do 0079).
         toast.error(financeErrorMessage(err, 'Erro ao salvar alterações.'));
      } finally {
         setIsSaving(false);
      }
   };

   // ─── Cancelar edição ───────────────────────────────────────
   const handleDisableEdit = useCallback(() => {
      setEditOn(false);
      setNextAction(charge?.next_action ?? '');
      setNextContactDate(charge?.next_contact_date ?? '');
      setCollectionStatus(charge?.collection_status ?? '');
      setDueDate(charge?.due_date ?? '');
      setEditInstallmentValue(charge?.installment_value ?? '');
      setEditOriginId(charge?.billingOrigin?.id ?? '');
      setNotes(charge?.notes ?? '');
   }, [charge]);

   // ─── Cancelar boleto — individual ─────────────────────────
   // `cancelReason` chega validado do modal (mín. 8 caracteres).
   const handleCancelSingle = async (cancelReason) => {
      setShowCancelModal(false);
      setIsSaving(true);
      try {
         await cancelCharge(id, cancelReason);
         toast.success('Boleto cancelado.');
         navigate('/billing');
      } catch (err) {
         toast.error(financeErrorMessage(err, 'Erro ao cancelar boleto.'));
      } finally {
         setIsSaving(false);
      }
   };

   // ─── Cancelar boleto — grupo ──────────────────────────────
   const handleCancelGroup = async (cancelReason) => {
      setShowCancelModal(false);
      setIsSaving(true);
      try {
         // O mesmo motivo vale para todas as parcelas canceladas.
         const { cancelled, errors } = await cancelChargeGroup(
            charge.installment_group_id,
            cancelReason
         );

         if (errors?.length) {
            toast.warn(
               `${cancelled.length} boleto(s) cancelado(s). ${errors.length} falhou: ${errors.map((e) => `#${e.id} (${e.error})`).join(', ')}`
            );
         } else {
            toast.success(
               `${cancelled.length} boleto(s) do grupo cancelados com sucesso.`
            );
         }

         navigate(`/billing/group/${charge.installment_group_id}`);
      } catch (err) {
         toast.error(financeErrorMessage(err, 'Erro ao cancelar grupo.'));
      } finally {
         setIsSaving(false);
      }
   };

   // ─── Adicionar atendimento ────────────────────────────────
   const handleAddUpdate = async () => {
      if (!updateContent.trim()) {
         toast.warn('Digite um conteúdo para o registro.');
         return;
      }
      setIsAddingUpdate(true);
      try {
         await axios.post(`/billing/${id}/updates`, {
            content: updateContent.trim(),
         });
         setUpdateContent('');
         await fetchTimeline(id);
         toast.success('Registro adicionado.');
      } catch {
         toast.error('Erro ao adicionar registro.');
      } finally {
         setIsAddingUpdate(false);
      }
   };

   // ─── Remover registro de atendimento ──────────────────────
   const handleDeleteUpdate = async (uid) => {
      if (!window.confirm('Remover este registro?')) return;
      try {
         await axios.delete(`/billing/${id}/updates/${uid}`);
         setFeed((prev) =>
            prev.filter((item) => !(item.type === 'update' && item.id === uid))
         );
         toast.success('Registro removido.');
      } catch (err) {
         const msg =
            err?.response?.data?.message ?? 'Erro ao remover registro.';
         toast.error(msg);
      }
   };

   // ─── Contadores por tipo ──────────────────────────────────
   const updatesCount = feed.filter((i) => i.type === 'update').length;
   const changesCount = feed.filter((i) => i.type === 'change').length;

   // ─── Itens filtrados pela tab ativa ───────────────────────
   const filteredFeed = useMemo(() => {
      if (activeTab === 'atendimentos')
         return feed.filter((i) => i.type === 'update');
      if (activeTab === 'alteracoes')
         return feed.filter((i) => i.type === 'change');
      return feed;
   }, [feed, activeTab]);

   // ─── Render ───────────────────────────────────────────────
   if (isLoading)
      return (
         <Container>
            <Loading isLoading />
         </Container>
      );

   return (
      <Container>
         <Loading isLoading={isSaving || isAddingUpdate} />

         <BackRow>
            <TextLink as="button" type="button" onClick={handleBack}>
               ← Voltar
            </TextLink>
         </BackRow>

         {/* ─── PageHeader ──────────────────────────────────── */}
         <PageHeader>
            <PageHeaderText>
               <PageTitle>
                  {isCreateMode
                     ? 'Novo Boleto'
                     : `Boleto #${charge?.id} — ${charge?.customer_name}`}
               </PageTitle>
               <PageSubtitle>{headerSubtitle}</PageSubtitle>
               {/* Lembrete persistente da empresa emissora: fica ao lado do
                   botão de emitir, que é onde a decisão é consumada. */}
               {isCreateMode && selectedBankAccount && (
                  <IssuerChip>
                     <span className="label">Emitindo por</span>
                     <span className="name">
                        {selectedBankAccount.account_name}
                     </span>
                     <span className="doc">
                        {formatDocForDisplay(
                           selectedBankAccount.account_doc_number
                        )}
                     </span>
                  </IssuerChip>
               )}
            </PageHeaderText>

            {/* Action bar de entidade (FC7 §G.7): destrutivo isolado à
                esquerda, ação principal do modo na ponta direita. */}
            <EntityActionBar>
               {isCreateMode && canCreate && (
                  <>
                     <GhostButton
                        type="button"
                        onClick={handleResetForm}
                        disabled={isSaving}
                     >
                        Limpar
                     </GhostButton>
                     <PrimaryButton
                        type="submit"
                        form="billing-form"
                        // Sem conta recebedora apta não há emissão possível —
                        // o backend recusaria. Trava aqui (o aviso no topo do
                        // form explica o porquê e aponta a tela de contas).
                        disabled={
                           isSaving ||
                           loadingBankAccounts ||
                           !aptBankAccounts.length
                        }
                        title={
                           !loadingBankAccounts && !aptBankAccounts.length
                              ? 'Nenhuma conta recebedora apta a emitir (ativa e com chave de integração)'
                              : undefined
                        }
                     >
                        {form.type === 'single'
                           ? 'Criar Boleto'
                           : 'Criar Parcelamento'}
                     </PrimaryButton>
                  </>
               )}

               {!isCreateMode && !editOn && (
                  <>
                     {canCancel &&
                        charge?.status !== 'CANCELLED' &&
                        charge?.status !== 'RECEIVED' && (
                           <>
                              <DangerButton
                                 type="button"
                                 onClick={() => setShowCancelModal(true)}
                                 disabled={isSaving}
                              >
                                 Cancelar Boleto
                              </DangerButton>
                              <ActionBarSpacer />
                           </>
                        )}
                     <CtaButton
                        type="button"
                        onClick={() => navigate('/billing/new')}
                     >
                        Novo Boleto
                     </CtaButton>
                     {charge?.boleto_url && (
                        <LinkButton
                           href={charge.boleto_url}
                           target="_blank"
                           rel="noreferrer"
                        >
                           Ver Boleto
                        </LinkButton>
                     )}
                     {canUpdate &&
                        charge?.status !== 'RECEIVED' &&
                        charge?.status !== 'CANCELLED' && (
                           <CtaButton
                              type="button"
                              onClick={() => setEditOn(true)}
                           >
                              Editar
                           </CtaButton>
                        )}
                  </>
               )}

               {!isCreateMode && editOn && (
                  <>
                     {canSettle &&
                        charge?.status !== 'RECEIVED' &&
                        charge?.status !== 'CANCELLED' && (
                           <CtaButton
                              type="button"
                              onClick={() => {
                                 setSettleDate(
                                    new Date().toISOString().substring(0, 10)
                                 );
                                 setShowSettleModal(true);
                              }}
                           >
                              Baixa Manual
                           </CtaButton>
                        )}
                     <GhostButton
                        type="button"
                        onClick={handleDisableEdit}
                        disabled={isSaving}
                     >
                        Cancelar
                     </GhostButton>
                     <PrimaryButton
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                     >
                        Salvar
                     </PrimaryButton>
                  </>
               )}
            </EntityActionBar>
         </PageHeader>

         {/* ─── Modal baixa manual ───────────────────────────── */}
         <Modal
            isOpen={showSettleModal}
            title="Registrar Recebimento Manual"
            onClose={() => setShowSettleModal(false)}
            maxWidth="400px"
            footer={
               <>
                  <GhostButton
                     type="button"
                     onClick={() => setShowSettleModal(false)}
                  >
                     Cancelar
                  </GhostButton>
                  <PrimaryButton
                     type="button"
                     onClick={handleSettle}
                     disabled={isSaving || !settleDate}
                  >
                     Confirmar Baixa
                  </PrimaryButton>
               </>
            }
         >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
               <p style={{ margin: 0, color: '#555', fontSize: 14 }}>
                  Confirme o recebimento em dinheiro deste boleto. Esta ação
                  também será registrada no ASAAS.
               </p>
               <label style={{ fontSize: 13, fontWeight: 600 }}>
                  Data de recebimento *
               </label>
               <input
                  type="date"
                  value={settleDate}
                  onChange={(e) => setSettleDate(e.target.value)}
                  max={new Date().toISOString().substring(0, 10)}
                  style={{
                     padding: '8px',
                     borderRadius: 4,
                     border: '1px solid #ccc',
                  }}
               />
            </div>
         </Modal>

         {/* ─── Modal cancelamento ───────────────────────────── */}
         {/* Motivo obrigatório (mín. 8 caracteres) — a validação mora no
             modal compartilhado; aqui só se decide o escopo do cancelamento. */}
         <CancelReasonModal
            isOpen={showCancelModal}
            onClose={() => setShowCancelModal(false)}
            onConfirm={handleCancelSingle}
            isBusy={isSaving}
            title="Cancelar cobrança"
            confirmLabel="Cancelar este boleto"
            description={
               charge?.installment_group_id
                  ? 'Este boleto pertence a um parcelamento. Escolha cancelar apenas este boleto ou todos os do grupo — o motivo informado vale para todos os boletos cancelados.'
                  : 'Tem certeza que deseja cancelar este boleto? Esta ação não pode ser desfeita.'
            }
            extraAction={
               charge?.installment_group_id
                  ? {
                       label: 'Cancelar todos do grupo',
                       onConfirm: handleCancelGroup,
                    }
                  : null
            }
         />

         {/* ─── Conferência final antes de emitir ──────────────── */}
         <IssueConfirmModal
            isOpen={showIssueConfirm}
            onClose={() => setShowIssueConfirm(false)}
            onConfirm={performCreate}
            account={selectedBankAccount}
            summary={issueSummary}
            isBusy={isSaving}
         />

         {/* ─── Modal de duplicata ──────────────────────────────── */}
         <Modal
            isOpen={!!duplicateChargeId}
            title="Boleto já existente"
            onClose={() => setDuplicateChargeId(null)}
            maxWidth="420px"
            footer={
               <>
                  <GhostButton
                     type="button"
                     onClick={() => setDuplicateChargeId(null)}
                  >
                     Fechar
                  </GhostButton>
                  <CtaButton
                     type="button"
                     onClick={() => {
                        setDuplicateChargeId(null);
                        navigate(`/billing/${duplicateChargeId}`);
                     }}
                  >
                     Ver boleto existente
                  </CtaButton>
               </>
            }
         >
            <p style={{ margin: 0, color: '#555', fontSize: 14 }}>
               Já existe um boleto registrado com estes dados (mesmo cliente,
               valor e vencimento). Nenhuma duplicata foi criada. Deseja
               visualizar o boleto existente?
            </p>
         </Modal>

         {/* ══════════════════════════════════════════════════════
             MODO CRIAÇÃO
         ══════════════════════════════════════════════════════ */}
         {isCreateMode && (
            <form id="billing-form" onSubmit={handleCreate}>
               <Section>
                  <SectionHeader>
                     <SectionTitle>Tipo de Cobrança</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                     {/* Conta recebedora indisponível: avisa e explica o
                         caminho, em vez de deixar o operador preencher tudo
                         para tomar erro no envio. */}
                     {!loadingBankAccounts &&
                        (bankAccountsError || !aptBankAccounts.length) && (
                           <IssuanceWarning>
                              {/* eslint-disable-next-line no-nested-ternary */}
                              {bankAccountsError ??
                                 (bankAccounts.length ? (
                                    <>
                                       Nenhuma conta recebedora está{' '}
                                       <strong>apta a emitir</strong>: as contas
                                       cadastradas estão sem chave de integração
                                       ativa. Cadastre a chave da ASAAS e teste
                                       a conexão em{' '}
                                       <PillLink to="/finance/bank-accounts">
                                          Contas bancárias
                                       </PillLink>
                                       .
                                    </>
                                 ) : (
                                    <>
                                       Não há{' '}
                                       <strong>conta recebedora ativa</strong>{' '}
                                       cadastrada, e toda cobrança precisa sair
                                       de uma conta. Cadastre a conta e a chave
                                       da ASAAS em{' '}
                                       <PillLink to="/finance/bank-accounts">
                                          Contas bancárias
                                       </PillLink>{' '}
                                       antes de emitir.
                                    </>
                                 ))}
                           </IssuanceWarning>
                        )}

                     <FormGrid $cols={12} $colsMobile={12}>
                        <Field $col={3} $colMobile={6}>
                           <label>Tipo</label>
                           <select
                              name="type"
                              value={form.type}
                              onChange={handleFormChange}
                           >
                              <option value="single">Boleto Avulso</option>
                              <option value="installment">Parcelamento</option>
                           </select>
                        </Field>
                     </FormGrid>
                  </SectionBody>
               </Section>

               {/* ─── Empresa emissora ───────────────────────────
                   Seção própria e em cartões, não um select no meio do
                   formulário: com dois CNPJs, "por qual empresa isso vai
                   faturar" é a decisão mais cara da tela e precisa estar
                   visível o tempo todo, não escondida atrás de um clique. */}
               <Section>
                  <SectionHeader>
                     <SectionTitle>Empresa emissora *</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                     {loadingBankAccounts ? (
                        <FieldHint>Carregando contas...</FieldHint>
                     ) : (
                        <>
                           <BankAccountCards
                              accounts={bankAccounts}
                              selectedId={form.bank_account_id}
                              onSelect={(accountId) =>
                                 handleFormChange({
                                    target: {
                                       name: 'bank_account_id',
                                       value: accountId,
                                    },
                                 })
                              }
                              disabled={!aptBankAccounts.length}
                           />
                           {/* Há conta apta, mas alguma está bloqueada: explica
                               o cartão acinzentado sem alarmar quem já pode
                               emitir. */}
                           {hasBlockedBankAccounts &&
                              aptBankAccounts.length > 0 && (
                                 <FieldHint style={{ marginTop: 10 }}>
                                    Contas sem chave de integração ativa não
                                    emitem e aparecem desabilitadas —{' '}
                                    <TextLink to="/finance/bank-accounts">
                                       gerenciar contas
                                    </TextLink>
                                    .
                                 </FieldHint>
                              )}
                        </>
                     )}
                  </SectionBody>
               </Section>

               <Section>
                  <SectionHeader>
                     <SectionTitle>Dados do Cliente</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                     <FormGrid $cols={12} $colsMobile={12}>
                        <Field $col={6} $colMobile={12}>
                           <label>Nome do Cliente *</label>
                           <input
                              type="text"
                              name="customer_name"
                              value={form.customer_name}
                              onChange={handleFormChange}
                              placeholder="Nome completo ou razão social"
                           />
                        </Field>
                        <Field $col={3} $colMobile={6}>
                           <label>CPF / CNPJ</label>
                           <InputCpfCnpj
                              type="text"
                              name="customer_doc"
                              value={form.customer_doc}
                              onChange={handleFormChange}
                              placeholder="000.000.000-00"
                           />
                        </Field>
                        <Field $col={3} $colMobile={6}>
                           <label>E-mail</label>
                           <input
                              type="email"
                              name="customer_email"
                              value={form.customer_email}
                              onChange={handleFormChange}
                              placeholder="cliente@email.com"
                              aria-invalid={
                                 !!createFieldErrors.customer_email || undefined
                              }
                           />
                           {createFieldErrors.customer_email && (
                              <FieldErrorText>
                                 {createFieldErrors.customer_email}
                              </FieldErrorText>
                           )}
                        </Field>
                        <Field $col={3} $colMobile={6}>
                           <label>Telefone / WhatsApp</label>
                           <input
                              type="text"
                              name="customer_phone"
                              value={form.customer_phone}
                              onChange={handleFormChange}
                              placeholder="(00) 00000-0000"
                              aria-invalid={
                                 !!createFieldErrors.customer_phone || undefined
                              }
                           />
                           {createFieldErrors.customer_phone && (
                              <FieldErrorText>
                                 {createFieldErrors.customer_phone}
                              </FieldErrorText>
                           )}
                        </Field>
                        <Field $col={3} $colMobile={6}>
                           <label>Nº Pedido / OS</label>
                           <input
                              type="text"
                              name="order_number"
                              value={form.order_number}
                              onChange={handleFormChange}
                              placeholder="Ex.: 9999"
                           />
                        </Field>
                        <Field $col={3} $colMobile={6}>
                           <label>Nº Nota Fiscal</label>
                           <input
                              type="text"
                              name="nfe_number"
                              value={form.nfe_number}
                              onChange={handleFormChange}
                              placeholder="Ex.: 1234"
                           />
                        </Field>
                        <Field $col={3} $colMobile={6}>
                           <label>Origem</label>
                           <select
                              name="origin_id"
                              value={form.origin_id}
                              onChange={handleFormChange}
                           >
                              <option value="">— Sem origem —</option>
                              {billingOrigins.map((origin) => (
                                 <option key={origin.id} value={origin.id}>
                                    {origin.name}
                                 </option>
                              ))}
                           </select>
                        </Field>
                        <Field $col={12} $colMobile={6}>
                           <label>Observações</label>
                           <input
                              type="text"
                              name="description"
                              value={form.description}
                              onChange={handleFormChange}
                              placeholder="Observações internas"
                           />
                        </Field>
                     </FormGrid>
                  </SectionBody>
               </Section>

               <Section>
                  <SectionHeader>
                     <SectionTitle>
                        {form.type === 'single'
                           ? 'Valor e Vencimento'
                           : 'Parcelamento'}
                     </SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                     <FormGrid $cols={12} $colsMobile={12}>
                        {form.type === 'single' ? (
                           <>
                              <Field $col={3} $colMobile={6}>
                                 <label>Valor (R$) *</label>
                                 <input
                                    type="number"
                                    name="installment_value"
                                    value={form.installment_value}
                                    onChange={handleFormChange}
                                    placeholder="0,00"
                                    min="0.01"
                                    step="0.01"
                                 />
                              </Field>
                              <Field $col={3} $colMobile={6}>
                                 <label>Vencimento *</label>
                                 <input
                                    type="date"
                                    name="due_date"
                                    value={form.due_date}
                                    onChange={handleFormChange}
                                 />
                              </Field>
                              <Field $col={6} $colMobile={12}>
                                 <label>Observações</label>
                                 <input
                                    type="text"
                                    name="notes"
                                    value={form.notes}
                                    onChange={handleFormChange}
                                    placeholder="Observações internas"
                                 />
                              </Field>
                           </>
                        ) : (
                           <>
                              <Field $col={3} $colMobile={6}>
                                 <label>Valor Total (R$) *</label>
                                 <input
                                    type="number"
                                    name="total_value"
                                    value={form.total_value}
                                    onChange={handleFormChange}
                                    placeholder="0,00"
                                    min="0.01"
                                    step="0.01"
                                 />
                              </Field>
                              <Field $col={2} $colMobile={6}>
                                 <label>Nº Parcelas *</label>
                                 <input
                                    type="number"
                                    name="installment_count"
                                    value={form.installment_count}
                                    onChange={handleFormChange}
                                    placeholder="Ex.: 3"
                                    min="2"
                                    max="24"
                                 />
                              </Field>
                              <Field $col={3} $colMobile={6}>
                                 <label>1º Vencimento *</label>
                                 <input
                                    type="date"
                                    name="first_due_date"
                                    value={form.first_due_date}
                                    onChange={handleFormChange}
                                 />
                              </Field>
                              {installmentDates.length > 0 && (
                                 <Field $col={12} $colMobile={12}>
                                    <label>
                                       Preview das parcelas —{' '}
                                       {formatCurrency(installmentValue)} cada
                                    </label>
                                    <InstallmentPreview>
                                       {installmentDates.map((date, index) => (
                                          // eslint-disable-next-line react/no-array-index-key
                                          <InstallmentRow key={index}>
                                             <InstallmentBadge>
                                                {index + 1}/
                                                {installmentDates.length}
                                             </InstallmentBadge>
                                             <span>{formatDate(date)}</span>
                                             <span
                                                style={{
                                                   marginLeft: 'auto',
                                                   fontWeight: 600,
                                                }}
                                             >
                                                {formatCurrency(
                                                   installmentValue
                                                )}
                                             </span>
                                          </InstallmentRow>
                                       ))}
                                    </InstallmentPreview>
                                 </Field>
                              )}
                           </>
                        )}
                     </FormGrid>
                  </SectionBody>
               </Section>
            </form>
         )}

         {/* ══════════════════════════════════════════════════════
             MODO VISUALIZAÇÃO / EDIÇÃO
         ══════════════════════════════════════════════════════ */}
         {!isCreateMode && charge && (
            <>
               {/* ─── Dados do boleto ──────────────────────────── */}
               <Section>
                  <SectionHeader>
                     <SectionTitle>Dados do Boleto</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                     <FormGrid $cols={12} $colsMobile={12}>
                        <Field $col={3} $colMobile={6}>
                           <label>Status</label>
                           <div style={{ paddingTop: 6 }}>
                              <StatusBadge
                                 value={charge.status}
                                 config={STATUS_CONFIG}
                              />
                           </div>
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>Situação</label>
                           {editOn ? (
                              <select
                                 value={collectionStatus}
                                 onChange={(e) =>
                                    setCollectionStatus(e.target.value)
                                 }
                              >
                                 <option value="ATIVA">Ativa</option>
                                 <option value="PAGA">Paga</option>
                                 <option value="CANCELADA">Cancelada</option>
                                 <option value="RENEGOCIADA">
                                    Renegociada
                                 </option>
                                 <option value="CONSULTH">Consulth</option>
                                 <option value="REVENDEDOR">Revendedor</option>
                                 <option value="JURIDICO">Jurídico</option>
                              </select>
                           ) : (
                              <div style={{ paddingTop: 6 }}>
                                 <StatusBadge
                                    value={charge.collection_status}
                                    config={COLLECTION_CONFIG}
                                 />
                              </div>
                           )}
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>Valor</label>
                           {editOn ? (
                              <input
                                 type="number"
                                 value={editInstallmentValue}
                                 onChange={(e) =>
                                    setEditInstallmentValue(e.target.value)
                                 }
                                 min="0.01"
                                 step="0.01"
                              />
                           ) : (
                              <input
                                 type="text"
                                 value={formatCurrency(
                                    charge.installment_value
                                 )}
                                 disabled
                              />
                           )}
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>Vencimento</label>
                           {editOn ? (
                              <input
                                 type="date"
                                 value={dueDate}
                                 onChange={(e) => setDueDate(e.target.value)}
                                 min={new Date(Date.now() + 86400000)
                                    .toISOString()
                                    .substring(0, 10)}
                              />
                           ) : (
                              <input
                                 type="text"
                                 value={formatDate(charge.due_date)}
                                 disabled
                              />
                           )}
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>Cliente</label>
                           <input
                              type="text"
                              value={charge.customer_name}
                              disabled
                           />
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>CPF / CNPJ</label>
                           <input
                              type="text"
                              value={charge.customer_doc ?? '—'}
                              disabled
                           />
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>Nº Pedido</label>
                           <input
                              type="text"
                              value={charge.order_number ?? '—'}
                              disabled
                           />
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>Nº Nota Fiscal</label>
                           <input
                              type="text"
                              value={charge.nfe_number ?? '—'}
                              disabled
                           />
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>Origem</label>
                           {editOn ? (
                              <select
                                 value={editOriginId}
                                 onChange={(e) =>
                                    setEditOriginId(e.target.value)
                                 }
                              >
                                 <option value="">— Sem origem —</option>
                                 {billingOrigins.map((origin) => (
                                    <option key={origin.id} value={origin.id}>
                                       {origin.name}
                                    </option>
                                 ))}
                              </select>
                           ) : (
                              <input
                                 type="text"
                                 value={charge.billingOrigin?.name ?? '—'}
                                 disabled
                              />
                           )}
                        </Field>

                        {/* Empresa emissora (handoff 0082 §2.2).
                            Antes este campo lia `asaas_account_label`, que vem
                            de uma env global do deploy: com dois CNPJs ele
                            passou a MENTIR, mostrando o mesmo rótulo para
                            boletos de empresas diferentes. Agora vem do
                            `bankAccount` real da cobrança. */}
                        <Field $col={3} $colMobile={6}>
                           <label>Empresa emissora</label>
                           <ReadOnlyBox>
                              <BankAccountTag
                                 account={charge.bankAccount}
                                 to={
                                    charge.bankAccount?.id
                                       ? `/finance/bank-accounts/${charge.bankAccount.id}`
                                       : null
                                 }
                              />
                           </ReadOnlyBox>
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>Responsável</label>
                           <input
                              type="text"
                              value={charge.responsible?.nome ?? '—'}
                              disabled
                           />
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>Dt. Recebimento</label>
                           <input
                              type="text"
                              value={formatDate(charge.payment_date)}
                              disabled
                           />
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>Forma Recebimento</label>
                           <input
                              type="text"
                              value={charge.payment_method ?? '—'}
                              disabled
                           />
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>Criado em</label>
                           <input
                              type="text"
                              value={formatDateTime(charge.created_at)}
                              disabled
                           />
                        </Field>

                        <Field $col={3} $colMobile={6}>
                           <label>Última alteração</label>
                           <input
                              type="text"
                              value={
                                 charge.updated_at &&
                                 charge.updated_at !== charge.created_at
                                    ? formatDateTime(charge.updated_at)
                                    : '—'
                              }
                              disabled
                           />
                        </Field>

                        <Field $col={4} $colMobile={12}>
                           <label>Próxima Ação</label>
                           <input
                              type="text"
                              value={nextAction}
                              onChange={(e) => setNextAction(e.target.value)}
                              placeholder="Ex.: Enviar 2ª via do boleto"
                              disabled={!editOn}
                           />
                        </Field>

                        <Field $col={2} $colMobile={6}>
                           <label>Próx. Contato</label>
                           <input
                              type="date"
                              value={nextContactDate}
                              onChange={(e) =>
                                 setNextContactDate(e.target.value)
                              }
                              disabled={!editOn}
                           />
                        </Field>

                        <Field $col={6} $colMobile={12}>
                           <label>Observações</label>
                           <input
                              type="text"
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              placeholder="Observações internas"
                              disabled={!editOn}
                           />
                        </Field>

                        {charge.installment_group_id && (
                           <Field $col={12} $colMobile={12}>
                              <label>&nbsp;</label>
                              <PillLink
                                 to={`/billing/group/${charge.installment_group_id}`}
                              >
                                 🔗 Boleto parcela {charge.installment_number}{' '}
                                 de {charge.installment_count} — Ver
                                 parcelamento #{charge.installment_group_id}
                              </PillLink>
                           </Field>
                        )}

                        {(charge.status === 'PENDING' ||
                           charge.status === 'OVERDUE') && (
                           <Field $col={12} $colMobile={12}>
                              <label>PIX copia e cola</label>
                              {!pixCode ? (
                                 <PrimaryButton
                                    type="button"
                                    onClick={handleGeneratePix}
                                    disabled={isPixLoading}
                                    style={{ width: 'fit-content' }}
                                 >
                                    {isPixLoading
                                       ? 'Gerando...'
                                       : 'Gerar PIX copia e cola'}
                                 </PrimaryButton>
                              ) : (
                                 <div style={{ position: 'relative' }}>
                                    <input
                                       type="text"
                                       value={pixCode}
                                       disabled
                                       style={{
                                          paddingRight: 40,
                                          fontFamily: 'monospace',
                                          fontSize: 12,
                                       }}
                                    />
                                    <IconButton
                                       type="button"
                                       onClick={handleCopyPix}
                                       title="Copiar código PIX"
                                       aria-label="Copiar código PIX"
                                       style={{
                                          position: 'absolute',
                                          right: 4,
                                          top: '50%',
                                          transform: 'translateY(-50%)',
                                       }}
                                    >
                                       <svg
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          aria-hidden="true"
                                       >
                                          <rect
                                             x="9"
                                             y="9"
                                             width="13"
                                             height="13"
                                             rx="2"
                                             ry="2"
                                          />
                                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                       </svg>
                                    </IconButton>
                                 </div>
                              )}
                           </Field>
                        )}
                     </FormGrid>
                  </SectionBody>
               </Section>

               {/* ─── Resumo do recebimento (handoff 0105) ─────────
                   Só existe quando o backend informou algum dos campos de
                   recebimento — título antigo (sem backfill) ou pendente não
                   ganha seção toda de "—". Campos SEMPRE disabled, inclusive
                   em modo edição: são espelho do que a ASAAS liquidou.
                   `null` ≠ 0: null = não informado ("—"); 0,00 = pago sem
                   acréscimo (R$ 0,00). */}
               {(charge.amount_received != null ||
                  charge.surcharge_amount != null ||
                  charge.net_amount_received != null) && (
                  <Section>
                     <SectionHeader>
                        <SectionTitle>Resumo do recebimento</SectionTitle>
                     </SectionHeader>
                     <SectionBody>
                        <FormGrid $cols={12} $colsMobile={12}>
                           <Field $col={3} $colMobile={6}>
                              <label>Valor do título</label>
                              <input
                                 type="text"
                                 value={formatBRL(charge.installment_value)}
                                 disabled
                              />
                           </Field>

                           <Field $col={3} $colMobile={6}>
                              <label>Valor recebido</label>
                              <input
                                 type="text"
                                 value={formatBRL(charge.amount_received)}
                                 disabled
                              />
                           </Field>

                           {/* "Juros e multa", nunca só "Juros": a ASAAS soma
                               os dois e não dá como separar (0105 §2.1). */}
                           <Field $col={3} $colMobile={6}>
                              <label>Juros e multa</label>
                              <input
                                 type="text"
                                 value={formatBRL(charge.surcharge_amount)}
                                 disabled
                              />
                           </Field>

                           <Field $col={3} $colMobile={6}>
                              <label>Valor líquido</label>
                              <input
                                 type="text"
                                 value={formatBRL(charge.net_amount_received)}
                                 disabled
                              />
                           </Field>

                           {boletoFee(charge) != null && (
                              <Field $col={3} $colMobile={6}>
                                 <label>Taxa do boleto</label>
                                 <input
                                    type="text"
                                    value={formatBRL(boletoFee(charge))}
                                    disabled
                                 />
                              </Field>
                           )}
                        </FormGrid>
                     </SectionBody>
                  </Section>
               )}

               {/* ─── Timeline: Atendimentos + Alterações ─────────── */}
               <Section>
                  <SectionHeader>
                     <SectionTitle>
                        Log de Alterações e Atendimento
                     </SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                     {canAddUpdate && activeTab === 'atendimentos' && (
                        <UpdateBox>
                           <textarea
                              value={updateContent}
                              onChange={(e) => setUpdateContent(e.target.value)}
                              placeholder="Registre aqui o atendimento, negociação ou observação..."
                              disabled={isAddingUpdate}
                           />
                           <div
                              style={{
                                 display: 'flex',
                                 justifyContent: 'flex-end',
                              }}
                           >
                              <SmallButton
                                 type="button"
                                 $intent="write"
                                 onClick={handleAddUpdate}
                                 disabled={
                                    isAddingUpdate || !updateContent.trim()
                                 }
                              >
                                 + Registrar
                              </SmallButton>
                           </div>
                        </UpdateBox>
                     )}

                     <TabBar>
                        <Tab
                           $active={activeTab === 'atendimentos'}
                           onClick={() => setActiveTab('atendimentos')}
                        >
                           Atendimentos
                           {updatesCount > 0 && (
                              <TabCount>{updatesCount}</TabCount>
                           )}
                        </Tab>
                        <Tab
                           $active={activeTab === 'alteracoes'}
                           onClick={() => setActiveTab('alteracoes')}
                        >
                           Alterações
                           {changesCount > 0 && (
                              <TabCount>{changesCount}</TabCount>
                           )}
                        </Tab>
                        <Tab
                           $active={activeTab === 'todos'}
                           onClick={() => setActiveTab('todos')}
                        >
                           Todos
                           {feed.length > 0 && (
                              <TabCount>{feed.length}</TabCount>
                           )}
                        </Tab>
                     </TabBar>

                     {loadingFeed ? (
                        <div
                           style={{
                              textAlign: 'center',
                              padding: '24px 0',
                              color: '#888',
                              fontSize: 14,
                           }}
                        >
                           Carregando histórico...
                        </div>
                     ) : filteredFeed.length === 0 ? (
                        <p style={{ color: '#888', fontSize: 13 }}>
                           {activeTab === 'atendimentos' &&
                              'Nenhum registro de atendimento ainda.'}
                           {activeTab === 'alteracoes' &&
                              'Nenhuma alteração registrada ainda.'}
                           {activeTab === 'todos' &&
                              'Nenhum registro encontrado.'}
                        </p>
                     ) : (
                        <TimelineFeed>
                           {filteredFeed.map((item) => (
                              <TimelineItem
                                 key={`${item.type}-${item.id}`}
                                 $type={item.type}
                              >
                                 <TimelineAvatar $type={item.type}>
                                    {item.type === 'update'
                                       ? getInitials(item.author?.nome)
                                       : '🔄'}
                                 </TimelineAvatar>
                                 <TimelineContent>
                                    <TimelineMeta>
                                       <strong>
                                          {item.author?.nome ?? 'Sistema'}
                                       </strong>
                                       {' · '}
                                       {formatDateTime(item.created_at)}
                                       {item.type === 'update' &&
                                          canDeleteUpdate && (
                                             <SmallButton
                                                type="button"
                                                $intent="danger"
                                                onClick={() =>
                                                   handleDeleteUpdate(item.id)
                                                }
                                                style={{ marginLeft: 12 }}
                                             >
                                                Remover
                                             </SmallButton>
                                          )}
                                       {item.type === 'change' &&
                                          item.source === 'webhook' && (
                                             <span
                                                style={{
                                                   marginLeft: 8,
                                                   fontSize: 10,
                                                   color: '#aaa',
                                                }}
                                             >
                                                via ASAAS
                                                {item.asaas_event
                                                   ? ` · ${item.asaas_event}`
                                                   : ''}
                                             </span>
                                          )}
                                    </TimelineMeta>
                                    <TimelineText>
                                       {item.type === 'update' ? (
                                          item.content
                                       ) : (
                                          <>
                                             <strong>{item.field_label}</strong>
                                             {': '}
                                             <span
                                                style={{
                                                   textDecoration:
                                                      'line-through',
                                                   color: '#aaa',
                                                }}
                                             >
                                                {item.old_value ?? '—'}
                                             </span>
                                             {' → '}
                                             <strong>
                                                {item.new_value ?? '—'}
                                             </strong>
                                          </>
                                       )}
                                    </TimelineText>
                                 </TimelineContent>
                              </TimelineItem>
                           ))}
                        </TimelineFeed>
                     )}
                  </SectionBody>
               </Section>
            </>
         )}
      </Container>
   );
}
